import { useState, useEffect, useRef } from "react";
import { Button, Text } from "@telegram-apps/telegram-ui";
import { motion, AnimatePresence } from "motion/react";
import hapticFeedback from "../../utils/haptics";
import ApiService from "../../handlers/apiService";
import {
  ManaReward,
  SingleManaRewardResponse,
  MultipleManaRewardResponse,
  LookInfo,
} from "../../handlers/types";
import { useGameStore, selectLooks, selectUser } from "../../store/gameStore";
import { useI18n } from "../../utils/i18n/I18nProvider"; // Add this line
import "./ManaAnimationComponent.css";

interface ManaAnimationComponentProps {
  selectedMultiplier: number;
  onComplete: (rewards: ManaReward[]) => void;
}

interface AnimationState {
  isAnimating: boolean;
  currentRound: number;
  totalRounds: number;
  currentReward: ManaReward | null;
  allRewards: ManaReward[];
  bubbleItems: ManaReward[];
  animationPhase: "loading" | "bubbles" | "popping" | "prize"; // Removed "complete"
  isSkipped: boolean;
  poppedBubbles: number[];
  winnerBubbleIndex: number;
  bubblePositions?: Array<{
    top: string;
    left?: string;
    right?: string;
    marginTop: string;
    marginLeft: string;
  }>;
  bubbleOpacities?: number[]; // Add this
  bubbleSizes?: number[]; // Add this line
}

// Reuse app's reward icon mapping pattern
const REWARD_ICONS: Record<string, string> = {
  EVL: "/assets/img/ui/EVL.webp",
  USDT: "/assets/img/ui/USDT.webp",
  MANA: "/assets/img/ui/mana.webp",
  COINS: "/assets/img/ui/coin.webp",
  ENERGY: "/assets/img/ui/energy_full.webp",
  PACK_BOOSTS: "/assets/img/ui/bundle.webp",
  HEAL_BOOST: "/assets/img/ui/heal.webp",
  EXPLOSION_BOOST: "/assets/img/ui/explosion.webp",
  RANDOM_LOOK: "/assets/img/ui/random_look.webp",
};

const bubbleVariants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      scale: { type: "spring", stiffness: 300, damping: 20 },
      opacity: { duration: 0.3 },
    },
  },
  popping: {
    scale: [1, 1.4, 0],
    opacity: [1, 0.8, 0],
    rotate: [0, 15, 0],
    filter: ["blur(0px)", "blur(2px)", "blur(4px)"],
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.6, 1],
    },
  },
  winner: {
    scale: [1, 1.15, 1.05],
    opacity: 1,
    rotate: [0, 5, 0],
    filter: [
      "drop-shadow(0 0 10px rgba(255, 185, 53, 0.5))",
      "drop-shadow(0 0 25px rgba(255, 185, 53, 0.8))",
      "drop-shadow(0 0 15px rgba(255, 185, 53, 0.6))",
    ],
    transition: {
      duration: 2,
      repeat: 3,
      ease: [0.4, 0, 0.6, 1],
    },
  },
} as any;

const prizeVariants = {
  hidden: {
    scale: 0.8,
    opacity: 0,
    y: 20,
  },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.6, 1],
    },
  },
} as any;

const prizeIconVariants = {
  spinning: {
    rotate: [180, 360],
    scale: [1, 1.2, 1],
    filter: [
      "drop-shadow(0 0 20px rgba(255, 185, 53, 0.8))",
      "drop-shadow(0 0 40px rgba(255, 185, 53, 1))",
      "drop-shadow(0 0 20px rgba(255, 185, 53, 0.8))",
    ],
    transition: {
      rotate: { duration: 1.5, ease: [0.4, 0, 0.6, 1] },
      scale: { duration: 1.5, repeat: 1, ease: [0.4, 0, 0.6, 1] },
      filter: { duration: 2, repeat: 1, ease: [0.4, 0, 0.6, 1] },
    },
  },
} as any;

// Add this helper function before the main component (around line 60)
const getLookById = (lookId: string, allLooks: LookInfo[]): LookInfo | null => {
  if (!allLooks || allLooks.length === 0) return null;

  return allLooks.find((look: LookInfo) => look.id === lookId) || null;
};

export function ManaAnimationComponent({
  selectedMultiplier,
  onComplete,
}: ManaAnimationComponentProps): JSX.Element {
  const { t } = useI18n(); // Add this line
  const updateMana = useGameStore.getState().updateMana;
  const user = useGameStore(selectUser);
  const isSkippedRef = useRef(false); // Add this ref
  const poppedBubblesRef = useRef<number[]>([]); // Track popped bubbles without re-renders
  const bubbleRefs = useRef<(HTMLDivElement | null)[]>([]); // Refs to bubble elements

  const [animationState, setAnimationState] = useState<AnimationState>({
    isAnimating: false,
    currentRound: 0,
    totalRounds: selectedMultiplier,
    currentReward: null,
    allRewards: [],
    bubbleItems: [],
    animationPhase: "loading",
    isSkipped: false,
    poppedBubbles: [],
    winnerBubbleIndex: 0,
    bubblePositions: [], // Initialize bubblePositions
    bubbleOpacities: [], // Initialize bubbleOpacities
    bubbleSizes: [], // Add this line
  });

  const callManaConvertAPI = async (
    manaAmount: string
  ): Promise<ManaReward[]> => {
    const apiService = ApiService.getInstance();
    const response = await apiService.api.manaConvertCreate({
      manaAmount: manaAmount as "1" | "5" | "10" | "50",
    });

    if ("reward" in response) {
      return [(response as SingleManaRewardResponse).reward];
    }

    if ("rewards" in response) {
      return (response as MultipleManaRewardResponse).rewards;
    }

    throw new Error("Invalid response format");
  };

  const updateManaAfterConsumption = (consumedAmount: number) => {
    try {
      if (user?.mana) {
        const updatedMana = {
          ...user.mana,
          current: Math.max(0, user.mana.current - consumedAmount),
        };
        updateMana(updatedMana);
      }
    } catch (error) {
      console.error("Failed to update mana after consumption:", error);
    }
  };

  const handleSkip = () => {
    if (isSkippedRef.current) return;

    // Add haptic feedback for skip action
    try {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred("light");
      }
    } catch (error) {
      // Ignore haptic feedback errors
    }

    // Set the ref first
    isSkippedRef.current = true;

    // Then update state
    setAnimationState((prev) => ({
      ...prev,
      isSkipped: true,
      animationPhase: "prize",
      isAnimating: false,
    }));
  };

  const startAnimationSequence = async () => {
    // Clear the popped bubbles ref and reset bubble elements
    poppedBubblesRef.current = [];
    bubbleRefs.current.forEach((bubble) => {
      if (bubble) {
        bubble.style.display = "";
        bubble.classList.remove("mana-animation__bubble--popping");
        // Re-add floating class
        const index = bubbleRefs.current.indexOf(bubble);
        if (index >= 0) {
          bubble.classList.add(`mana-animation__bubble--floating-${index % 3}`);
        }
      }
    });

    setAnimationState((prev) => ({
      ...prev,
      isAnimating: true,
      currentRound: 1,
      animationPhase: "loading",
      poppedBubbles: [],
      winnerBubbleIndex: 0,
      bubblePositions: [], // Clear positions on new animation
      bubbleOpacities: [], // Clear opacities on new animation
    }));

    try {
      // Always call API immediately for all multipliers
      const manaAmount =
        selectedMultiplier === 1
          ? "1"
          : selectedMultiplier === 5
          ? "5"
          : selectedMultiplier === 10
          ? "10"
          : "50";

      const rewards = await callManaConvertAPI(manaAmount);

      // Update mana after successful API call
      updateManaAfterConsumption(selectedMultiplier);

      // Now decide on animation based on multiplier
      if (selectedMultiplier === 1) {
        // Show bubble animation with pre-fetched rewards
        await performBubbleAnimation(rewards);
      } else {
        // Go directly to prize reveal for X5-50
        setAnimationState((prev) => ({
          ...prev,
          allRewards: rewards,
          animationPhase: "prize",
          isAnimating: false,
        }));
      }
    } catch (error) {
      console.error("Animation sequence failed:", error);
      // On error, just complete with empty rewards
      onComplete([]);
    }
  };

  const performBubbleAnimation = async (
    rewards: ManaReward[]
  ): Promise<void> => {
    // Create decoy rewards - expanded list (without USDT since we'll add it separately)
    const allDecoyRewards: ManaReward[] = [
      { type: "COINS", amount: 100 },
      { type: "EVL", amount: 50 },
      { type: "MANA", amount: 2 },
      { type: "ENERGY", amount: 1 },
      { type: "HEAL_BOOST", amount: 1 },
      { type: "EXPLOSION_BOOST", amount: 1 },
      { type: "PACK_BOOSTS", amount: 1 },
      { type: "RANDOM_LOOK", amount: 1 },
    ];

    // Randomly select 4 decoy rewards from the list
    const shuffledDecoys = [...allDecoyRewards].sort(() => 0.5 - Math.random());
    const selectedDecoys = shuffledDecoys.slice(0, 4);

    // Always include USDT as the 5th decoy reward
    const decoyRewards = [
      ...selectedDecoys,
      { type: "USDT", amount: 1 } as ManaReward,
    ];

    // Randomly choose position for the winning bubble (0-5)
    const winnerIndex = Math.floor(Math.random() * 6);

    // Create bubbles array with winner at random position
    const bubbleItems: ManaReward[] = [];
    let decoyIndex = 0;

    for (let i = 0; i < 6; i++) {
      if (i === winnerIndex) {
        bubbleItems.push(rewards[0]);
      } else {
        bubbleItems.push(decoyRewards[decoyIndex]);
        decoyIndex++;
      }
    }

    // Generate positions once with small random variations
    const bubblePositions = generateBubblePositionsWithVariation();

    // Generate random opacities once and store them
    const bubbleOpacities = Array.from({ length: 6 }, (_, index) => {
      return (index = 0.4 + Math.random() * 0.4);
    });

    // Generate random bubble sizes (100-130 range for increased average size)
    const bubbleSizes = Array.from({ length: 6 }, () => {
      return 90 + Math.random() * 40; // Random size between 90-130
    });

    // Phase 1: Show bubbles
    setAnimationState((prev) => ({
      ...prev,
      bubbleItems,
      currentReward: rewards[0],
      allRewards: rewards,
      animationPhase: "bubbles",
      winnerBubbleIndex: winnerIndex,
      isAnimating: false,
      bubblePositions,
      bubbleOpacities,
      bubbleSizes, // Store random sizes in state
    }));

    // Phase 2: Bubbles float (2 seconds) - check skip frequently
    for (let i = 0; i < 12; i++) {
      // 20 * 100ms = 2000ms
      if (isSkippedRef.current) return; // Exit immediately if skipped
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (isSkippedRef.current) return;

    // Phase 3: Sequential bubble popping
    // No need to update state here - just pop the bubbles directly

    // Pop bubbles one by one
    const bubblesToPop: number[] = [];
    for (let i = 0; i < bubbleItems.length; i++) {
      if (i !== winnerIndex) {
        bubblesToPop.push(i);
      }
    }

    // Shuffle the order of popping
    for (let i = bubblesToPop.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [bubblesToPop[i], bubblesToPop[j]] = [bubblesToPop[j], bubblesToPop[i]];
    }

    // Pop each bubble - check skip between each pop
    for (let j = 0; j < bubblesToPop.length; j++) {
      if (isSkippedRef.current) return;

      const bubbleIndex = bubblesToPop[j];

      // Add popping class directly to the bubble element
      const bubbleElement = bubbleRefs.current[bubbleIndex];
      if (bubbleElement) {
        bubbleElement.classList.add("mana-animation__bubble--popping");
        bubbleElement.classList.remove(
          `mana-animation__bubble--floating-${bubbleIndex % 3}`
        );
      }

      // Wait for the pop animation to complete
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Hide the bubble after animation
      if (bubbleElement) {
        bubbleElement.style.display = "none";
      }

      // Add to popped bubbles ref
      poppedBubblesRef.current = [...poppedBubblesRef.current, bubbleIndex];

      // Add haptic feedback
      try {
        if (hapticFeedback.impactOccurred.isAvailable()) {
          hapticFeedback.impactOccurred("light");
        }
      } catch (error) {
        // Ignore haptic feedback errors
      }

      // Wait 500ms with frequent skip checks
      for (let k = 0; k < 3; k++) {
        // 5 * 100ms = 500ms
        if (isSkippedRef.current) return;
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    if (isSkippedRef.current) return;

    // Small delay to ensure last bubble is fully hidden
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Wait before showing prize - check for skip
    for (let i = 0; i < 6; i++) {
      // 10 * 100ms = 1000ms
      if (isSkippedRef.current) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (isSkippedRef.current) return;

    // Add haptic feedback for prize reveal
    try {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred("medium");
      }
    } catch (error) {
      // Ignore haptic feedback errors
    }

    // Phase 4: Prize reveal - ONLY if not skipped
    if (!isSkippedRef.current) {
      setAnimationState((prev) => ({
        ...prev,
        animationPhase: "prize",
      }));
    }
  };

  // Simple function with fixed positions + small random variations
  const generateBubblePositionsWithVariation = () => {
    // Fixed base positions that we know work well
    const basePositions = [
      { top: 15, left: 20 }, // Top left
      { top: 15, right: 20 }, // Top right
      { top: 30, left: 10 }, // Middle left
      { top: 30, right: 10 }, // Middle right
      { top: 45, left: 30 }, // Lower left
      { top: 45, right: 30 }, // Lower right
    ];

    return basePositions.map((pos) => {
      // Add small random variation (±5% for position, ±3% for sides)
      const topVariation = (Math.random() - 0.5) * 10; // ±5%
      const sideVariation = (Math.random() - 0.5) * 6; // ±3%

      const result: any = {
        top: `${pos.top + topVariation}%`,
        marginTop: "0px",
        marginLeft: "0px",
      };

      // Handle left or right positioning
      if ("left" in pos) {
        result.left = `${(pos as any).left + sideVariation}%`;
      } else {
        result.right = `${(pos as any).right + sideVariation}%`;
      }

      return result;
    });
  };

  useEffect(() => {
    startAnimationSequence();

    // Reset skip ref when component unmounts
    return () => {
      isSkippedRef.current = false;
    };
  }, []);

  const BubbleSequence = () => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mana-animation__bubbles-container"
      >
        {animationState.bubbleItems.map((item, index) => {
          const isWinner = index === animationState.winnerBubbleIndex;

          // Use stored random bubble size instead of winner-based sizing
          const bubbleSize = animationState.bubbleSizes?.[index] || 110;

          // Use stored positions (no more calculateBubblePosition calls)
          const position = animationState.bubblePositions?.[index] || {
            top: "20%",
            left: "20%",
            marginTop: "0px",
            marginLeft: "0px",
          };

          // Remove winner-based z-index preference - use index-based instead
          const zIndex = 50 + index;

          // Use stored opacity instead of recalculating
          const bubbleOpacity = animationState.bubbleOpacities?.[index] || 0.6;

          return (
            <motion.div
              key={`bubble-${index}`}
              ref={(el) => {
                bubbleRefs.current[index] = el;
              }}
              variants={bubbleVariants}
              initial={
                animationState.animationPhase === "bubbles" ? "hidden" : false
              }
              animate={
                animationState.animationPhase === "prize" && isWinner
                  ? "winner"
                  : "visible"
              }
              transition={{
                delay:
                  animationState.animationPhase === "bubbles"
                    ? index * 0.15
                    : 0,
              }}
              className={`mana-animation__bubble mana-animation__bubble--floating-${
                index % 3
              }`}
              style={{
                position: "absolute",
                width: bubbleSize,
                height: bubbleSize,
                zIndex,
                ...position,
                animationDelay: `${index * 0.2}s`,
              }}
            >
              {/* Bubble illustration background with random opacity */}
              <div
                className="mana-animation__bubble-background"
                style={{
                  width: bubbleSize,
                  height: bubbleSize,
                  backgroundImage: 'url("/assets/img/ui/bubble.webp")',
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  opacity: bubbleOpacity,
                }}
              />

              {/* Bubble Content - adjusted positioning */}
              <div
                className="mana-animation__bubble-content"
                style={{
                  transform: "translateY(-5px)",
                }}
              >
                <motion.img
                  src={REWARD_ICONS[item.type] || "/assets/img/ui/coin.webp"}
                  alt={item.type}
                  className="mana-animation__bubble-icon"
                  whileHover={{ scale: 1.1 }}
                  style={{
                    width: bubbleSize * 0.4,
                    height: bubbleSize * 0.4,
                  }}
                />
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    );
  };

  const handlePrizeConfirm = async () => {
    // Add haptic feedback for prize confirmation
    try {
      if (hapticFeedback.impactOccurred.isAvailable()) {
        hapticFeedback.impactOccurred("medium");
      }
    } catch (error) {
      // Ignore haptic feedback errors
    }

    // Refresh user data from server to keep it in sync
    // This ensures the balance is accurate for other parts of the app
    try {
      await useGameStore.getState().refreshUserData();
    } catch (error) {
      console.error("Failed to refresh user data after mana operation:", error);
      // Continue with reward completion even if server refresh fails
    }

    // Complete with the collected rewards instead of just going back
    onComplete(animationState.allRewards);
  };

  const LoadingDisplay = () => {
    // Create multiple bubbles with random positioning
    const bubbles = Array.from({ length: 8 }, (_, index) => index);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="mana-animation__loading-display"
      >
        <Text className="mana-animation__loading-text">
          {t("mana.boiling_mana")}
        </Text>

        {/* Bubbles container - positioned closer to text */}
        <motion.div className="mana-animation__loading-bubbles">
          {bubbles.map((index) => {
            // Random horizontal positioning around the text
            const randomX = (Math.random() - 0.5) * 200; // Random spread of 200px
            const randomStartY = Math.random() * 20; // Random starting height near text
            const randomDelay = Math.random() * 0.6; // Random delay up to 0.6s

            // Start some bubbles at different phases
            const initialScale = index < 4 ? Math.random() * 0.8 : 0;
            const initialOpacity = index < 4 ? Math.random() * 0.6 : 0;
            const initialY =
              index < 4 ? randomStartY - Math.random() * 30 : randomStartY;

            return (
              <motion.div
                key={index}
                className="mana-animation__loading-bubble"
                initial={{
                  scale: initialScale,
                  opacity: initialOpacity,
                  x: randomX,
                  y: initialY,
                }}
                animate={{
                  scale: [initialScale, 1, 0],
                  opacity: [initialOpacity, 1, 0],
                  y: [initialY, initialY - 80, initialY - 160],
                  x: randomX + (Math.random() - 0.5) * 40, // Slight horizontal drift
                }}
                transition={{
                  duration: 0.8,
                  repeat: Infinity,
                  delay: randomDelay,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  repeatDelay: Math.random() * 0.2, // Random repeat delay
                }}
              >
                <img
                  src="/assets/img/ui/bubble.webp"
                  alt="Loading bubble"
                  className="mana-animation__loading-bubble-image"
                />
              </motion.div>
            );
          })}
        </motion.div>
      </motion.div>
    );
  };

  const PrizeReveal = () => {
    const allRewards = animationState.allRewards;
    const userLooks = useGameStore(selectLooks);

    if (!allRewards || allRewards.length === 0) return null;

    const title = t("mana.your_rewards");

    // Add these helper functions
    const renderRewardIcon = (reward: ManaReward, className: string) => {
      // Special handling for RANDOM_LOOK with specificId
      if (reward.type === "RANDOM_LOOK" && reward.specificId) {
        const lookData = getLookById(reward.specificId, userLooks);

        if (lookData) {
          // Display the actual character look with the same animation as other icons
          return (
            <motion.div
              className={`${className} mana-animation__character-look`}
              variants={prizeIconVariants}
              animate="spinning"
            >
              <div
                className="mana-animation__character-look-sprite"
                style={{
                  background: `url(/assets/img/characters/${lookData.spriteKey}.webp) -12px -8px`,
                }}
                aria-label={`Character Look: ${lookData.nameKey}`}
                role="img"
              />
            </motion.div>
          );
        }
      }

      // Default icon handling for all other rewards with original animation
      return (
        <motion.img
          src={REWARD_ICONS[reward.type] || "/assets/img/ui/coin.webp"}
          alt={reward.type}
          className={className}
          variants={prizeIconVariants}
          animate="spinning"
        />
      );
    };

    const getRewardTitle = (reward: ManaReward): string => {
      if (reward.type === "RANDOM_LOOK" && reward.specificId) {
        const lookData = getLookById(reward.specificId, userLooks);
        if (lookData) {
          const rarityKey = lookData.rarity?.toLowerCase() || "common";
          return `${t(`looks.rarity.${rarityKey}`)} ${t("mana.look_suffix")}`;
        }
        return t("mana.look_suffix");
      }
      // Use localized reward titles
      return t(`mana.rewards.${reward.type}`, { fallback: reward.type });
    };

    // Add key to prevent re-animation
    const animationKey = `${animationState.animationPhase}-${animationState.isSkipped}`;

    // Original prize reveal animation for normal success path
    return (
      <motion.div key={animationKey}>
        {/* Backdrop/overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="mana-animation__prize-backdrop"
        />
        <motion.div
          variants={prizeVariants}
          initial="hidden"
          animate="visible"
          className="mana-animation__prize-animation"
        >
          <div className="mana-animation__prize-burst">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <Text className="mana-animation__prize-title">{title}</Text>
            </motion.div>

            {/* Show all rewards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mana-animation__prize-rewards-list"
            >
              {allRewards.map((reward, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="mana-animation__prize-reward-item"
                >
                  {renderRewardIcon(
                    reward,
                    "mana-animation__prize-reward-icon"
                  )}
                  <div className="mana-animation__prize-reward-text">
                    <Text className="mana-animation__prize-reward-title">
                      {getRewardTitle(reward)}
                    </Text>
                    <Text className="mana-animation__prize-reward-amount">
                      {reward.amount}
                    </Text>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Use telegram-ui Button consistently */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mana-animation__prize-confirm-container"
            >
              <Button
                onClick={handlePrizeConfirm}
                size="l"
                className="mana-animation__prize-confirm-button"
              >
                {t("mana.claim_rewards")}
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="mana-animation">
      <div className="mana-animation__background" />

      {/* Animation Scene */}
      <div className="mana-animation__scene">
        <AnimatePresence mode="wait">
          {animationState.animationPhase === "loading" && (
            <LoadingDisplay key="loading" />
          )}
          {(animationState.animationPhase === "bubbles" ||
            animationState.animationPhase === "popping") && (
            <BubbleSequence key="bubbles" />
          )}
          {animationState.animationPhase === "prize" && (
            <PrizeReveal key="prize" />
          )}
        </AnimatePresence>
      </div>

      {/* Skip Button - only show during bubbles/popping for X1 */}
      <AnimatePresence>
        {!animationState.isSkipped &&
          selectedMultiplier === 1 &&
          (animationState.animationPhase === "bubbles" ||
            animationState.animationPhase === "popping") && (
            <motion.div
              className="mana-animation__skip-container"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.5 }}
            >
              <Button
                onClick={handleSkip}
                size="l"
                className="mana-animation__skip-button-bottom"
                disabled={animationState.isSkipped}
              >
                {t("common.skip")}
              </Button>
            </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
}
